/**
 * Nuxt plugin for Commerce Layer integration.
 *
 * This plugin:
 * 1. Skips initialization for federated environments
 * 2. Fetches user session under specific rendering conditions
 * 3. Initializes Commerce Layer client (with OOB token if present in query)
 *
 * @type {import('nuxt/app').NuxtPlugin}
 * @name nuxt:commerce-layer
 */
// @ts-ignore
export default defineNuxtPlugin(async nuxtApp => {
  // Skip initialization for federated environments
  if (useRuntimeConfig().public.isFederated) return

  /**
   * Fetch user session under specific conditions:
   * - Not server-rendered
   * - Pre-rendered
   * - Cached
   */
  if (!nuxtApp.payload.serverRendered) {
    await useUserSession().fetch()
  } else if (
    Boolean(nuxtApp.payload.prerenderedAt) ||
    Boolean(nuxtApp.payload.isCached)
  ) {
    await useUserSession().fetch()
  }

  /**
   * Activate the OOB checkout session early, before useCL() builds the client,
   * so the client is created with the OOB sales-channel token instead of the
   * logged-in user's token.
   *
   * The token is taken from the query string or from
   * localStorage on a payment-gateway return (PayPal/Adyen redirect back to
   * `/checkout/adyen|paypal?redirectResult=...`, if the query params are no
   * longer present).
   */
  if (process.client) {
    const route = useRoute()
    const queryAccessToken = route.query.accessToken
    const queryOrderId = route.query.order_id

    const accessToken =
      typeof queryAccessToken === 'string'
        ? queryAccessToken
        : (readStoredOobCheckoutAccessToken() ?? undefined)
    const orderId =
      typeof queryOrderId === 'string'
        ? queryOrderId
        : (readStoredOobCheckoutOrderId() ?? undefined)

    if (
      typeof accessToken === 'string' &&
      typeof orderId === 'string' &&
      isCommerceLayerSalesChannelJwt(accessToken) &&
      !isCommerceLayerJwtExpired(accessToken)
    ) {
      const { applyCheckoutAccessToken } = useOrderOnBehalf()
      try {
        await applyCheckoutAccessToken(accessToken, orderId)
      } catch {
        // Non-blocking: token validation/activation failed, will be retried by page
      }
    }
  }

  /**
   * Initialize Commerce Layer client.
   * @type {Object} Commerce Layer client instance
   */
  const { cl } = await useCL()

  /**
   * Provide Commerce Layer client to the Nuxt application.
   * @returns {Object} Provides the Commerce Layer client
   */
  return {
    provide: {
      cl,
    },
  }
})
